<template>
  <div class="node" v-show="visible" :class="{ active: isActive, root }" :style="computedStyle">
    <component :is="currPage" v-bind="nodeInfo.props" :ref="nodeInfo.id">
      <template slot="ListContainer" v-if="isListContainer" slot-scope="listCell">
        <pre-node :info="nodeChild[0]" :scope="listCell" @node-mounted="nodeMounted" />
      </template>
      <template v-if="isPageContainer">
        <pre-node slot="PageContainer" :scope="scope" :info="item" v-for="item in nodeChild" :key="item.id" @node-mounted="nodeMounted" />
      </template>
      <template v-if="nodeInfo.child && !!slots">
        <pre-node v-for="(item,index) in nodeInfo.child"
                  :slot-style="slots(index) && slots(index).style"
                  :slot="slots(index) && slots(index).name"
                  :key="item.id"
                  :stacked="nodeInfo.stack"
                  :scope="scope"
                  :info="item"
                  @node-mounted="nodeMounted"
        />
      </template>
    </component>
    <template v-if="hasChild && !isListContainer && !isPageContainer && !slots">
      <pre-node v-for="item in nodeInfo.child" :key="item.id" :stacked="nodeInfo.stack" :scope="scope" :info="item" @node-mounted="nodeMounted" />
    </template>
  </div>
</template>

<script>
import cLoader from 'src/extend/componentLoader'
import BaseComponent from 'src/extend/BaseComponent'
import BaseNode from 'src/extend/BaseNode'
import cloneDeep from 'lodash/cloneDeep'
import Vue from 'vue'

export default {
  name: 'PreNode',
  mixins: [BaseNode, BaseComponent],
  props: {
    scope: {
      type: Object
    },
    stacked: {
      type: Boolean,
      default: true
    },
    info: {
      required: true,
      type: Object
    },
    slotStyle: {
      type: Object,
      default: null
    },
    root: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      oldId: '',
      isActive: false,
      currPage: '',
      oldStyle: {},
      nodeInfo: (() => {
        if (this.scope) {
          return cloneDeep(this.info)
        } else {
          return this.info
        }
      })(),
      slots: this.info.hasSlot ? (i) => `slot${i}` : false,
      mountedLength: 0,
      selfMounted: false
    }
  },
  computed: {
    visible: {
      // getter
      get: function () {
        if (this.nodeInfo) {
          return this.nodeInfo.visible
        } else {
          return false
        }
      },
      // setter
      set: function (newValue) {
        this.nodeInfo.visible = newValue
      }
    },
    nodeChild () {
      return this.nodeInfo.child || []
    },
    hasChild () {
      return this.nodeChild && this.nodeChild.length > 0
    },
    isListContainer () {
      return this.nodeInfo && this.nodeInfo.type.indexOf('ListContainer') > -1
    },
    isPageContainer () {
      return this.nodeInfo && this.nodeInfo.type.indexOf('PageContainer') > -1
    },
  },
  watch: {
    'nodeInfo.visible': function (val, old) {
      if (old === false && val) {
        this.doAfterMounted()
      }
    },
    info: {
      deep: true,
      handler: function (newVal) {
        var info = {}
        if (this.scope) {
          info = cloneDeep(newVal)
        } else {
          info = newVal
        }
        this.nodeInfo = info
      }
    },
    scope: {
      handler: function (val) {
        if (!val) return
        this.$nextTick(this.passScopeToComponentInstance)
      },
      deep: true
    }
  },
  beforeDestroy: function () {
    delete window.$_nodePrecomponents[this.oldId]
  },
  mounted () {
    this.oldId = this.nodeInfo.id
    this.registerNodeToGlobal()
    this.doLoad()
  },
  methods: {
    cloneDeep: cloneDeep,
    initNodeEvent () {
      var me = this
      if (this.initEvented) return
      this.initEvented = true
      this.nodeInfo.events && this.nodeInfo.events.forEach(element => {
        me.getComponent(me.nodeInfo.id, true).$el.addEventListener(element.event, (event) => {
          me.getComponent(me.nodeInfo.id, true).oncallExecute(element.callList, [event])
        })
      })
    },
    /**
      * 获取组件对象
      * @param {String} id  组件id
      * @param {boolean} child 是否获取组件内部元素
      */
    getComponent (id, child) {
      var component = window.$_nodePrecomponents[id]
      if (child && component) {
        return component.$refs[id]
      }
      return component
    },
    registerNodeToGlobal () {
      window.$_nodePrecomponents = window.$_nodePrecomponents || {}
      window.$_nodePrecomponents[this.nodeInfo.id] = this
    },
    passScopeToComponentInstance () {
      const scope = this.scope
      const $childInstance = this.$refs[this.nodeInfo.id]
      scope && $childInstance && $childInstance.$set($childInstance.$data, 'inscope', scope.data || scope)
    },
    async doLoad () {
      if (this.nodeInfo.type === 'node') return
      const component = await cLoader.load(this.nodeInfo)
      this.slots = this.calcSlots(component, this.nodeInfo.props)
      if (!this.isNodeRegisted(this.nodeInfo.id)) {
        this.registerNode(this.nodeInfo)
      }
      this.currPage = this.nodeInfo.id

      await this.doAfterMounted()

      this.selfMounted = true

      if (!this.nodeInfo.child?.length || this.mountedLength >= this.nodeInfo.child.length) {
        this.$emit('node-mounted', this.nodeInfo)
      }
    },
    async doAfterMounted () {
      await this.$nextTick()
      this.passScopeToComponentInstance()
      this.runAnimate()
      this.initNodeEvent()
    },
    isNodeRegisted (tag) {
      return Boolean(Vue.component(tag))
    },
    isNodeStartRegiste (tag) {
      return tag in (window.$_nodePrecomponents || {})
    },
    nodeMounted() {
      this.mountedLength++
      if (this.selfMounted && this.mountedLength >= this.nodeInfo.child.length) {
        this.$emit('node-mounted', this.nodeInfo);
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
  .node {
    position: relative;
    font-size 16px;
    &::before {
      content: ''
      display: block;
      overflow: hidden;
      height: 0;
      width: 1px;
    }
  }
</style>
